import React from 'react';
import { formatPrice } from '../Prices/priceLookupShared';
import { formatNumber } from '../../../shared/numberFormat';
import { formatDate } from '../../../shared/tenantClock';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { NO_CARRIER_KEY } from '../../../shared/shippingCarriage';

const linkButton = { padding: '5px 12px', borderRadius: 6, border: '1px solid #1976d2', background: '#eaf2fb', color: '#1976d2', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const cell = { padding: '2px 10px' };
// The selected method's column.
const selectedCell = { background: '#eaf2fb' };
const selectButton = { padding: '2px 10px', borderRadius: 6, border: '1px solid #1976d2', background: '#fff', color: '#1976d2', fontSize: 11, fontWeight: 700, cursor: 'pointer' };
// Each method's column heading, broken where it reads best.
const HEADING_LINES = {
  rate_card: ['MyPost Business', 'Rate Card'],
  pac: ['Postage Assessment', 'Calculator (PAC) API'],
  partner: ['MyPost Business', 'Partner API'],
};
const warning = { fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 6 };

// The inputs an estimate was made from (issue #327) — the delivery
// address, Service, Packaging, Options and the lines' products, units and
// quantities. The estimate keeps the key it was made with; a different key
// now means the Quote or Sales Order changed since, so Save asks whether to
// re-estimate (useReestimateOnSave.js, issue #437).
export function estimateInputsKey({ addressId, shippingServiceId, packagingId, selectedOptions, lines }) {
  return JSON.stringify({
    a: String(addressId || ''),
    s: String(shippingServiceId || ''),
    p: String(packagingId || ''),
    o: (selectedOptions || []).map((o) => String(o.shipping_option_id)).sort(),
    l: (lines || []).map((l) => [String(l.productId), String(l.uomId), Number(l.qty) || 0]),
  });
}

// Whether the shipping charge differs from the estimate it came from —
// staff typed over it.
export function chargeTypedOver(estimate, shippingCost) {
  if (!estimate) return false;
  if (shippingCost === null || shippingCost === undefined) return true;
  return Math.abs(Number(shippingCost) - Number(estimate.charge)) > 0.00005;
}

// Why the charge was typed over the estimate, under the totals' Shipping row,
// as a line's Reason sits under its price. Read-only once the charge is locked.
export function ShippingOverrideReason({ value, onChange, locked }) {
  if (locked) {
    return value ? <div style={{ fontSize: 11, color: '#5a6b85', textAlign: 'right', marginTop: -2, marginBottom: 4 }}>{value}</div> : null;
  }
  return (
    <div style={{ textAlign: 'right', marginBottom: 4, paddingRight: 19 }}>
      <input
        className="styled-input"
        type="text"
        placeholder="Reason (optional)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 140, fontSize: 11, padding: '2px 6px', height: 22 }}
      />
    </div>
  );
}

// Whether a document is going ahead with no shipping charge on a Service
// that has a Carrier (issue #351) — Accept Order, and a Quote's Convert,
// which Accepts the new order, warn first. A No Carrier Service, like
// Customer Pickup, has nothing to charge.
export function shippingNotAdded(service, shippingCost) {
  const carrier = service?.supplier;
  return !!carrier && carrier.system_key !== NO_CARRIER_KEY && (shippingCost === null || shippingCost === undefined);
}

// Estimate Shipping on a Quote whose Service is Australia Post's (issue
// #327): staff pick the Packaging the goods would go in, and Australia
// Post prices one parcel of the goods' weight in it. Shows the postage, each
// Option's Australia Post price next to the Option's own charge, the
// Packaging's own cost, the Shipping Charge Rounding (Sales Settings) when
// it added anything, and the total, ex or inc GST following the Quote's
// Ex GST / Inc GST switch (viewInc), like the Shipping charge it fills
// (GstAwareCostField, same rounding); the estimate itself is kept ex GST.
// On screen only — never printed. The Sales Order has the same estimate
// (issue #351).
// Every Australia Post method switched on (issue #328) has its own column,
// side by side; the selected one is highlighted, and onSelectMethod(key)
// selects another. A method that couldn't price it says why in its column
// and can't be selected. An estimate saved before #328 has one column.
const PACKAGING_GROUPS = [
  { type: 'box', label: 'Boxes' },
  { type: 'mailer', label: 'Mailers' },
  { type: 'satchel', label: 'Satchels' },
];

function QuoteShippingEstimate({
  packagings, packagingId, onPackagingChange, estimate, typedOver,
  selectedOptions, onEstimate, onSelectMethod, estimating, error, locked,
  gstApplicable, viewInc, gstRate,
}) {
  const rate = Number(gstRate) || 0;
  // An ex GST figure on the switch's basis.
  const money = (ex) => formatPrice(viewInc ? Math.round(Number(ex) * (1 + rate / 100) * 100) / 100 : ex);
  // Only Packaging that can go out the door on its own (not an inner box),
  // grouped by type; the one already picked always stays.
  const picked = (p) => String(p.id) === String(packagingId || '');
  const shown = packagings.filter((p) => picked(p)
    || (p.is_active !== false && (p.packaging_type !== 'box' || p.can_ship_alone !== false)));
  const groups = PACKAGING_GROUPS
    .map((g) => ({ ...g, items: shown.filter((p) => p.packaging_type === g.type).sort((a, b) => a.name.localeCompare(b.name)) }))
    .filter((g) => g.items.length);
  const chargeOf = (optionId) => selectedOptions.find((o) => String(o.shipping_option_id) === String(optionId))?.cost;
  const methods = estimate?.methods?.length
    ? estimate.methods
    : (estimate ? [{ key: 'saved', name: 'Australia Post', ok: true, postage: estimate.postage, options: estimate.options, total: estimate.total }] : []);
  const selectedKey = estimate?.methods?.length ? estimate.selected_method : 'saved';
  // A charge typed over the estimate isn't any method's price, so no column
  // is highlighted and the one it came from says Overridden.
  const highlight = (key) => (key === selectedKey && !typedOver ? selectedCell : {});

  return (
    <div style={{ marginTop: 16, padding: '10px 12px', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8, maxWidth: 860 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6 }}>Shipping Estimate</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Packaging</label>
          <select
            className="styled-input"
            value={packagingId}
            onChange={(e) => onPackagingChange(e.target.value)}
            disabled={locked}
            style={{ fontSize: 14, width: 340 }}
          >
            <option value="">(pick one)</option>
            {groups.map((g) => (
              <optgroup key={g.type} label={g.label}>
                {g.items.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        {!locked && (
          <button type="button" style={linkButton} onClick={onEstimate} disabled={estimating} title="Price shipping by every Australia Post method switched on and fill in the Shipping charge with the selected price">
            {estimating ? 'Estimating...' : (estimate ? 'Re-estimate' : 'Estimate Shipping')}
          </button>
        )}
      </div>

      {error && <div style={{ ...warning, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca' }}>{error}</div>}

      {estimate && (
        <div style={{ fontSize: 13, marginTop: 8 }}>
          <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
            {estimate.packaging?.name} · {formatNumber(estimate.weight_kg, { maxDecimals: 3 })} kg · estimated {formatDate(estimate.estimated_at)}
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th />
                {methods.map((m) => {
                  const selected = m.key === selectedKey;
                  return (
                    <th key={m.key} style={{ ...cell, textAlign: 'right', verticalAlign: 'bottom', fontWeight: 600, maxWidth: 170, ...highlight(m.key) }}>
                      <div style={{ fontSize: 12 }}>
                        {HEADING_LINES[m.key]
                          ? HEADING_LINES[m.key].map((line) => <div key={line} style={{ whiteSpace: 'nowrap' }}>{line}</div>)
                          : m.name}
                      </div>
                      <TaxBasisLine>{gstApplicable ? (viewInc ? 'inc GST' : 'ex GST') : null}</TaxBasisLine>
                      {methods.length > 1 && (selected ? (
                        <div style={{ fontSize: 11, color: typedOver ? '#b45309' : '#1976d2', fontWeight: 700, marginTop: 2 }}>{typedOver ? 'Overridden' : 'Selected'}</div>
                      ) : m.ok && !locked && onSelectMethod ? (
                        <button type="button" style={{ ...selectButton, marginTop: 2 }} onClick={() => onSelectMethod(m.key)} title={`Use the ${m.name} price for the Shipping charge`}>
                          Select
                        </button>
                      ) : null)}
                    </th>
                  );
                })}
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '2px 16px 2px 0' }}>Postage</td>
                {methods.map((m) => (
                  <td key={m.key} style={{ ...cell, textAlign: 'right', ...highlight(m.key) }}>
                    {m.ok ? money(m.postage) : <span style={{ color: '#7a5c00', fontSize: 12 }}>{m.message}</span>}
                  </td>
                ))}
                <td />
              </tr>
              {(estimate.options || []).map((o) => (
                <tr key={o.shipping_option_id}>
                  <td style={{ padding: '2px 16px 2px 0' }}>{o.description}</td>
                  {methods.map((m) => {
                    const cost = m.ok ? (m.options || []).find((x) => String(x.shipping_option_id) === String(o.shipping_option_id))?.cost : undefined;
                    return (
                      <td key={m.key} style={{ ...cell, textAlign: 'right', ...highlight(m.key) }}>
                        {!m.ok ? '—' : (cost === null || cost === undefined ? 'Not priced' : money(cost))}
                      </td>
                    );
                  })}
                  <td style={{ padding: '2px 0 2px 16px', color: '#666', fontSize: 12 }}>
                    {chargeOf(o.shipping_option_id) !== undefined ? `Option charge ${money(chargeOf(o.shipping_option_id))}` : ''}
                  </td>
                </tr>
              ))}
              {estimate.packaging_cost !== undefined && (
                <tr>
                  <td style={{ padding: '2px 16px 2px 0' }}>{estimate.packaging?.name || 'Packaging'}</td>
                  {methods.map((m) => (
                    <td key={m.key} style={{ ...cell, textAlign: 'right', ...highlight(m.key) }}>
                      {!m.ok ? '—' : (estimate.packaging_cost === null ? 'No cost' : money(estimate.packaging_cost))}
                    </td>
                  ))}
                  <td style={{ padding: '2px 0 2px 16px', color: '#666', fontSize: 12 }}>
                    {estimate.packaging_cost === null ? 'Link a Consumable Product with a Cost Price on Packaging' : ''}
                  </td>
                </tr>
              )}
              {methods.some((m) => m.ok && Number(m.rounding) > 0) && (
                <tr>
                  <td style={{ padding: '2px 16px 2px 0' }}>Rounding</td>
                  {methods.map((m) => (
                    <td key={m.key} style={{ ...cell, textAlign: 'right', ...highlight(m.key) }}>
                      {m.ok ? money(m.rounding || 0) : '—'}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
              <tr style={{ borderTop: '1px solid #e3e7ee', fontWeight: 700 }}>
                <td style={{ padding: '4px 16px 2px 0' }}>Total</td>
                {methods.map((m) => (
                  <td key={m.key} style={{ ...cell, paddingTop: 4, textAlign: 'right', ...highlight(m.key) }}>
                    {m.ok ? money(m.total) : '—'}
                  </td>
                ))}
                <td />
              </tr>
            </tbody>
          </table>
          {methods.filter((m) => m.ok && m.note).map((m) => (
            <div key={m.key} style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{methods.length > 1 ? `${m.name}: ` : ''}{m.note}</div>
          ))}
          {typedOver && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Estimate Overridden: the Shipping charge was typed in. The estimate was {money(estimate.charge)}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QuoteShippingEstimate;
